/**
 * The attribute names `r-text` reads for show more / show less.
 *
 * These sit beside the existing text attributes rather than sharing a name
 * with any other component: RuleCMS publishes one page per component-scope
 * attribute and requires exactly one owning component. Reuse happens a level
 * down, where the composer resolves an attribute to an *editor* by name —
 * the limit reuses the plain-text field and the labels reuse the multi-line
 * text field.
 *
 * Nothing here duplicates a column style attribute. Spacing, colour and
 * typography come from Column Styles on the root element; the attributes
 * below are only the character budget and the two toggle labels.
 */

export const TEXT_SHOW_MORE_AFTER_ATTRIBUTE = 'text-show-more-after-3-resolutions';
export const TEXT_SHOW_MORE_LABEL_ATTRIBUTE = 'text-show-more-label-3-resolutions';
export const TEXT_SHOW_LESS_LABEL_ATTRIBUTE = 'text-show-less-label-3-resolutions';

export const DEFAULT_TEXT_SHOW_MORE_LABEL = 'Show more';
export const DEFAULT_TEXT_SHOW_LESS_LABEL = 'Show less';

type Attributes = { [key: string]: any } | undefined;

/**
 * Collapses a label to one line the way a toggle's visible text is one line:
 * authored newlines become spaces, a `{br|}` becomes a space, then trim.
 * Markers for bold, italic and styling stay in the string so render can
 * honour them; link markers stay too and print as plain words when no link
 * list is passed.
 */
const resolveOneLineLabel = (value: unknown): string => {
  const text = typeof value === 'string' ? value : String(value);
  return text
    .replace(/\{br\|\}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Character budget for collapsing the text. A positive whole number turns
 * the feature on; anything else — unset, null, empty, zero, a decimal, a
 * non-digit string — leaves the text as it always was.
 *
 * The editor stores a string in the plain-text field (the number editor is
 * pixel-specific), but a hand-written or migrated config may carry a real
 * number; both mean the same thing here. Digit-only strings after trim are
 * accepted, including leading zeros (`'07'` → 7); `'0'` and `'00'` are still
 * off because the value is not >= 1.
 */
export const resolveTextShowMoreAfter = (componentAttributes: Attributes): number | undefined => {
  const value = componentAttributes?.[TEXT_SHOW_MORE_AFTER_ATTRIBUTE];

  if (typeof value === 'number') {
    if (Number.isFinite(value) && Number.isInteger(value) && value >= 1) {
      return value;
    }
    return undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      const parsed = Number(trimmed);
      if (parsed >= 1) {
        return parsed;
      }
    }
  }

  return undefined;
};

/**
 * Label on the toggle while the text is collapsed. Unset or null falls back
 * to "Show more". A blank after trimming also falls back — a toggle with no
 * words would leave the rest of the text unreachable.
 */
export const resolveTextShowMoreLabel = (componentAttributes: Attributes): string => {
  const value = componentAttributes?.[TEXT_SHOW_MORE_LABEL_ATTRIBUTE];
  if (value === undefined || value === null) {
    return DEFAULT_TEXT_SHOW_MORE_LABEL;
  }
  const normalized = resolveOneLineLabel(value);
  return normalized === '' ? DEFAULT_TEXT_SHOW_MORE_LABEL : normalized;
};

/**
 * Label on the toggle while the text is expanded. Unset or null falls back
 * to "Show less". A blank after trimming also falls back — the composer's
 * text editor deletes a cleared setting rather than storing `''`, so a blank
 * here is the same kind of empty as Show more, not a distinct mode.
 */
export const resolveTextShowLessLabel = (componentAttributes: Attributes): string => {
  const value = componentAttributes?.[TEXT_SHOW_LESS_LABEL_ATTRIBUTE];
  if (value === undefined || value === null) {
    return DEFAULT_TEXT_SHOW_LESS_LABEL;
  }
  const normalized = resolveOneLineLabel(value);
  return normalized === '' ? DEFAULT_TEXT_SHOW_LESS_LABEL : normalized;
};
